import React, { useEffect, useState } from "react";
import { Calculator, RefreshCw, Flame } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useUnits } from "@/lib/useUnits";
import { kgToLb, lbToKg, cmToIn, inToFtIn, ftInToCm } from "@/lib/units";
import NumField from "@/components/NumField";

const inputCls =
  "w-full bg-obsidian-900 border border-obsidian-600 px-2.5 py-2 rounded-sm focus:border-blaze focus:outline-none focus:ring-1 focus:ring-blaze text-sm";
const labelCls = "block text-[10px] uppercase tracking-widest text-zinc-500 mb-1";

const ACTIVITIES = [
  ["sedentary", "Sedentary"],
  ["light", "Light (1-3d/wk)"],
  ["moderate", "Moderate (3-5d/wk)"],
  ["active", "Active (6-7d/wk)"],
  ["very_active", "Very active"],
];
const GOALS = [
  ["fat_loss", "Fat loss"],
  ["muscle_gain", "Muscle gain"],
  ["strength", "Strength"],
  ["recomp", "Recomp"],
  ["maintenance", "Maintenance"],
];

/**
 * MacroCalculatorCard — dashboard macro calculator for clients AND coaches.
 * Coaches get a stateless calculator (handy tool for in-person clients).
 * Clients can additionally save the new stats to their profile and refresh
 * their AI meal plan against the fresh macros in one tap.
 */
export const MacroCalculatorCard = ({ onMacrosSaved }) => {
  const { user } = useAuth();
  const { units } = useUnits();
  const isImperial = units === "imperial";
  const isCoach = Boolean(user?.is_coach);
  const navigate = useNavigate();

  const [form, setForm] = useState({
    sex: "male", age: 30, heightCm: 175, weightKg: 80,
    activity_level: "moderate", fitness_goal: "fat_loss",
  });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [savingPlan, setSavingPlan] = useState(false);

  // Prefill from the client's existing profile.
  useEffect(() => {
    if (isCoach) return;
    (async () => {
      try {
        const r = await api.get("/profile");
        const p = r.data?.profile;
        if (p) {
          const w = Number(p.weight_kg);
          const h = Number(p.height_cm);
          setForm({
            sex: p.sex || "male",
            age: p.age || 30,
            // Defensive clamp: a corrupt profile value shouldn't paint an
            // absurd number into the form.
            heightCm: h >= 90 && h <= 250 ? h : 175,
            weightKg: w >= 25 && w <= 350 ? w : 80,
            activity_level: p.activity_level || "moderate",
            fitness_goal: p.fitness_goal || "fat_loss",
          });
        }
      } catch {}
    })();
  }, [isCoach]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Height display helpers (imperial → ft/in split).
  const { ft, in: inches } = inToFtIn(cmToIn(Number(form.heightCm) || 0));

  const payload = () => ({
    sex: form.sex,
    age: Number(form.age),
    height_cm: Number(form.heightCm),
    weight_kg: Number(form.weightKg),
    activity_level: form.activity_level,
    fitness_goal: form.fitness_goal,
  });

  const validate = () => {
    const age = Number(form.age);
    const h = Number(form.heightCm);
    const w = Number(form.weightKg);
    if (!age || age < 13 || age > 100) return "Age must be between 13 and 100";
    if (!h || h < 90 || h > 250) return "Height looks off — double-check it";
    if (!w || w < 25 || w > 350) return `Weight looks off (${isImperial ? Math.round(kgToLb(w)) + " lbs" : w + " kg"}) — double-check it`;
    return null;
  };

  const calculate = async () => {
    const err = validate();
    if (err) return toast.error(err);
    setBusy(true);
    try {
      const r = await api.post("/macros/preview", payload());
      setResult(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Calculation failed");
    } finally {
      setBusy(false);
    }
  };

  const saveAndRefreshPlan = async () => {
    const err = validate();
    if (err) return toast.error(err);
    setSavingPlan(true);
    try {
      const m = await api.post("/macros/update", payload());
      setResult(m.data);
      onMacrosSaved?.(m.data);
      toast.success("Stats saved — macros updated");
      try {
        await api.post("/meals/generate");
        toast.success("Meal plan refreshed to your new macros");
        navigate("/meals");
      } catch (e) {
        if (e?.response?.status === 402) {
          toast.error(e.response.data?.detail || "Meal planning is a Premium feature");
        } else {
          toast.error(e?.response?.data?.detail || "Meal plan refresh failed");
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save stats");
    } finally {
      setSavingPlan(false);
    }
  };

  return (
    <div className="obs-card" data-testid="macro-calculator-card">
      <div className="flex items-center gap-3 mb-4">
        <Calculator className="h-5 w-5 text-blaze" />
        <div>
          <div className="overline">Macro calculator</div>
          <h3 className="font-heading text-2xl uppercase">Dial in your numbers</h3>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-3">
        <div>
          <label className={labelCls}>Sex</label>
          <select data-testid="macro-sex" value={form.sex} onChange={set("sex")} className={inputCls}>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div>
          <label className={labelCls}>Age</label>
          <input data-testid="macro-age" type="number" min={13} max={100} value={form.age} onChange={set("age")} className={inputCls} />
        </div>
        {isImperial ? (
          <div>
            <label className={labelCls}>Height (ft / in)</label>
            <div className="flex gap-2">
              <NumField
                data-testid="macro-height-ft"
                value={ft}
                onCommit={(raw) => setForm((f) => ({ ...f, heightCm: ftInToCm(raw, inches) }))}
                className={inputCls}
              />
              <NumField
                data-testid="macro-height-in"
                value={Math.round(inches)}
                onCommit={(raw) => setForm((f) => ({ ...f, heightCm: ftInToCm(ft, raw) }))}
                className={inputCls}
              />
            </div>
          </div>
        ) : (
          <div>
            <label className={labelCls}>Height (cm)</label>
            <NumField
              data-testid="macro-height-cm"
              value={form.heightCm === "" ? "" : Math.round(Number(form.heightCm) || 0)}
              onCommit={(raw) => setForm((f) => ({ ...f, heightCm: raw }))}
              className={inputCls}
            />
          </div>
        )}
        <div>
          <label className={labelCls}>Weight ({isImperial ? "lbs" : "kg"})</label>
          <NumField
            data-testid="macro-weight"
            step="0.1"
            value={isImperial ? Math.round(kgToLb(Number(form.weightKg)) * 10) / 10 : form.weightKg}
            onCommit={(raw) =>
              setForm((f) => ({
                ...f,
                weightKg: isImperial ? lbToKg(Number(raw)) : raw,
              }))
            }
            className={inputCls}
          />
        </div>
        <div>
          <label className={labelCls}>Activity</label>
          <select data-testid="macro-activity" value={form.activity_level} onChange={set("activity_level")} className={inputCls}>
            {ACTIVITIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Goal</label>
          <select data-testid="macro-goal" value={form.fitness_goal} onChange={set("fitness_goal")} className={inputCls}>
            {GOALS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
      </div>

      <button
        type="button"
        data-testid="macro-calculate-btn"
        onClick={calculate}
        disabled={busy}
        className="blaze-btn px-5 py-2.5 text-xs inline-flex items-center gap-2 disabled:opacity-50"
      >
        <Flame className="h-3.5 w-3.5" /> {busy ? "Calculating…" : "Calculate macros"}
      </button>

      {result && (
        <div className="mt-4" data-testid="macro-result">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-3">
            {[
              ["BMR", result.bmr, "kcal"],
              ["TDEE", result.tdee, "kcal"],
              ["Calories", result.calories, "kcal"],
              ["Protein", result.protein_g, "g"],
              ["Carbs", result.carbs_g, "g"],
              ["Fat", result.fat_g, "g"],
            ].map(([label, val, unit]) => (
              <div key={label} className="stat-tile">
                <div className="overline">{label}</div>
                <div className="font-data text-xl">
                  {val} <span className="text-xs text-zinc-500">{unit}</span>
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-zinc-400 leading-relaxed mb-3">{result.reasoning}</p>

          {!isCoach && (
            <button
              type="button"
              data-testid="macro-save-refresh-btn"
              onClick={saveAndRefreshPlan}
              disabled={savingPlan}
              className="ghost-btn px-5 py-2.5 text-xs inline-flex items-center gap-2 disabled:opacity-50"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${savingPlan ? "animate-spin" : ""}`} />
              {savingPlan ? "Refreshing plan…" : "Save stats & refresh meal plan"}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default MacroCalculatorCard;
